import { AgentPageEntity } from '../../types/agentHarness';
import { getEntityById, nullSafeGet } from '../../utils/DataAccessUtils';
import { oscarEntityLabel } from './OscarPageContext';

/**
 * How the context bar describes a shared record: "WO #34234 · <title>" over "<location>".
 *
 * Display only — Oscar is sent the id and reads the record itself. Everything here comes from the
 * Redux slice the record's own page already loaded, so nothing is fetched; a record that is not in
 * the store (or a field it lacks) just leaves that part out and the label stands alone.
 */
export interface OscarEntityDescription {
	/** "WO #34234". */
	label: string;
	title?: string;
	subtitle?: string;
}

/** "Club #458", the way the location page's overtitle names it. */
const storeLine = (location: any): string | undefined => {
	const storeId = nullSafeGet('storeId', location);
	if (!storeId) return undefined;
	const typeName = nullSafeGet('locationType.name', location);
	return `${typeName ? `${typeName} ` : ''}#${storeId}`;
};

/** "Club #458 · North Vancouver Capilano Fit4Less", or whichever half the location has. */
const locationLine = (location: any): string | undefined => {
	if (!location) return undefined;
	return (
		[storeLine(location), nullSafeGet('name', location)].filter(Boolean).join(' · ') || undefined
	);
};

const describe = (
	label: string,
	title: string | undefined,
	subtitle?: string
): OscarEntityDescription => ({
	label,
	...(title ? { title } : {}),
	...(subtitle ? { subtitle } : {}),
});

/** The description for `entity`, from whichever slice holds its type. */
export const describeOscarEntity = (
	entity: AgentPageEntity,
	state: any
): OscarEntityDescription => {
	const label = oscarEntityLabel(entity);
	const record = (slice: string) => getEntityById(entity.id, nullSafeGet(slice, state), null);

	switch (entity.type) {
		case 'work_order': {
			const workOrder = record('work_orders');
			return describe(
				label,
				nullSafeGet('title', workOrder),
				locationLine(nullSafeGet('location', workOrder))
			);
		}
		case 'invoice': {
			const invoice = record('invoices');
			// The same title the invoice page header shows.
			const title =
				nullSafeGet('workOrder.title', invoice) ||
				nullSafeGet('title', invoice) ||
				nullSafeGet('project.title', invoice);
			const location =
				nullSafeGet('location', invoice) || nullSafeGet('workOrder.location', invoice);
			return describe(label, title, locationLine(location));
		}
		case 'proposal': {
			const quote = record('proposals');
			return describe(
				label,
				nullSafeGet('workOrder.title', quote),
				locationLine(nullSafeGet('workOrder.location', quote))
			);
		}
		case 'rfp': {
			const rfp = record('requests_for_proposal');
			return describe(label, nullSafeGet('title', rfp), locationLine(nullSafeGet('location', rfp)));
		}
		case 'supplier_facility': {
			// The supplier page loads a supplier it already works with through the private network
			// (`distinctRecords`, keyed by supplier facility id) and never reads the facility itself.
			const relationships = nullSafeGet('supplier_private_networks.distinctRecords', state);
			const relationship = relationships ? relationships[entity.id] : null;
			const name =
				nullSafeGet('name', record('supplier_facilities')) ||
				nullSafeGet('supplierFacility.name', relationship);
			return describe(label, name);
		}
		case 'asset': {
			const asset = record('assets');
			return describe(
				label,
				nullSafeGet('name', asset),
				locationLine(nullSafeGet('location', asset))
			);
		}
		case 'location': {
			const location = record('locations');
			return describe(label, nullSafeGet('name', location), storeLine(location));
		}
		case 'project': {
			const project = record('projects');
			return describe(label, nullSafeGet('title', project));
		}
		case 'walkthrough': {
			const walkthrough = record('walkthroughs');
			return describe(
				label,
				nullSafeGet('title', walkthrough),
				locationLine(nullSafeGet('location', walkthrough))
			);
		}
		case 'site_survey': {
			const siteSurvey = record('site_surveys');
			return describe(
				label,
				nullSafeGet('title', siteSurvey),
				locationLine(nullSafeGet('location', siteSurvey))
			);
		}
		case 'budget': {
			const budget = record('budgets');
			return describe(label, nullSafeGet('title', budget));
		}
		default:
			return describe(label, undefined);
	}
};
